import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, PhoneCall, PhoneForwarded, Package, Users, Calendar, ClipboardCheck, Clock, CalendarDays, Loader2, X, ExternalLink, ShoppingCart, Settings2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fetchTenantAiPhone, patchTenantForwardPhone } from "@/lib/api";

/**
 * Sprint 3 — tenant AI-telefon panel.
 * Business-friendly summary of the ElevenLabs integration. NO agent IDs,
 * webhook URLs, HMAC states, or secrets — read from
 * `GET /api/settings/ai-phone` which is server-side sanitized.
 */
export default function AIPhonePanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            setData(await fetchTenantAiPhone());
        } catch {
            toast.error("Kunne ikke hente AI-telefon status");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24" data-testid="ai-phone-loading">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
        );
    }
    if (!data) return null;

    const u = data.usage || {};
    const s = data.period_stats || {};
    const healthCls = {
        ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
        ready: "bg-blue-50 text-blue-700 border-blue-200",
        warning: "bg-amber-50 text-amber-700 border-amber-200",
        error: "bg-rose-50 text-rose-700 border-rose-200",
        blocked: "bg-rose-50 text-rose-700 border-rose-300",
        unknown: "bg-slate-100 text-slate-600 border-slate-200",
        not_configured: "bg-slate-100 text-slate-600 border-slate-200",
        idle: "bg-slate-100 text-slate-600 border-slate-200",
    }[data.health] || "bg-slate-100 text-slate-600 border-slate-200";
    const dotCls = {
        ok: "bg-emerald-500", ready: "bg-blue-500", warning: "bg-amber-500",
        error: "bg-rose-500", blocked: "bg-rose-500", unknown: "bg-slate-400",
    }[data.health] || "bg-slate-400";

    const pct = Math.max(0, Math.min(100, u.pct || 0));
    const barCls = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-gradient-to-r from-violet-600 to-violet-500";
    const iconCls = "w-4 h-4 text-slate-400 shrink-0";

    return (
        <div className="space-y-6" data-testid="ai-phone-panel">
            {/* Page header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Integrationer</h1>
                <p className="text-sm text-slate-500 mt-1">Forbind MyServiceHelper med de tjenester, du bruger i hverdagen.</p>
            </div>

            {/* Main card */}
            <article className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="ai-phone-card">
                {/* Top: icon + title + health */}
                <header className="px-6 pt-6 pb-4 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-md shrink-0">
                        <PhoneCall className="w-6 h-6 text-white" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold text-slate-900">AI-telefon</h2>
                        <p className="text-sm text-slate-500 mt-0.5 max-w-md">
                            Din AI-telefon tager imod kundeopkald og opretter automatisk opgaver i MyServiceHelper.
                        </p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${healthCls}`}
                          data-testid="ai-phone-health">
                        <span className={`inline-block w-2 h-2 rounded-full ${dotCls}`} /> {data.health_label}
                    </span>
                </header>

                {data.health_reason && data.health !== "blocked" && (
                    <div className="mx-6 mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600" data-testid="ai-phone-health-reason">
                        Årsag: {data.health_reason}
                    </div>
                )}

                {/* Blocked notice */}
                {data.blocked_notice && (
                    <div className="mx-6 mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="ai-phone-blocked-notice">
                        {data.blocked_notice}
                    </div>
                )}

                {/* Meta bar */}
                <div className="px-6 py-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-5 gap-6" data-testid="ai-phone-meta">
                    <MetaCell icon={Phone} label="Telefonnummer" value={data.phone_number || "—"} testid="meta-phone" />
                    <MetaCell icon={Package} label="Pakke" value={data.package_label || "Ukendt"} muted={!data.package_label} testid="meta-package" />
                    <MetaCell icon={Users} label="Kapacitet" value={data.capacity_label} testid="meta-capacity" />
                    <MetaCell icon={PhoneForwarded} label="Viderestilling"
                        value={data.forward_phone_masked || "Ikke opsat"}
                        muted={!data.forward_phone_configured}
                        testid="meta-forward" />
                    <MetaCell icon={Calendar} label="Næste fakturering" value={data.next_billing_label || "Ukendt"} muted={!data.next_billing_label} testid="meta-next-billing" />
                </div>

                {/* Body split: Forbrug + Denne periode */}
                <div className="px-6 py-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50/60">
                    {/* Left — Forbrug */}
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">
                            FORBRUG {u.period_label && <span className="text-slate-700"> · {u.period_label.toUpperCase()}</span>}
                        </p>
                        <div className="flex items-baseline gap-3 mb-3">
                            <span className="text-4xl font-bold text-slate-900 tabular-nums" data-testid="ai-phone-used">
                                {new Intl.NumberFormat("da-DK").format(u.used_minutes || 0)}
                            </span>
                            <span className="text-lg text-slate-500 tabular-nums">
                                / {new Intl.NumberFormat("da-DK").format(u.included_minutes || 0)} minutter
                            </span>
                            <span className="ml-auto text-sm text-slate-500 tabular-nums" data-testid="ai-phone-pct">{pct}%</span>
                        </div>
                        <div className="w-full h-2.5 rounded-full bg-slate-200 overflow-hidden mb-2" data-testid="ai-phone-usage-bar">
                            <div className={`h-full ${barCls} transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-sm text-slate-600 mb-6" data-testid="ai-phone-remaining">
                            {new Intl.NumberFormat("da-DK").format(u.remaining_minutes || 0)} minutter tilbage
                        </p>
                        <ul className="space-y-1.5 text-sm">
                            <BreakdownRow dotColor="violet" label={`Inkluderet i ${data.package_label || "pakken"}`} value={`${u.base_minutes || 0} minutter`} testid="bd-base" />
                            {u.monthly_extra_minutes > 0 && (
                                <BreakdownRow dotColor="violet" label="Ekstra abonnementsminutter" value={`${u.monthly_extra_minutes} minutter`} testid="bd-monthly" />
                            )}
                            {u.one_time_minutes_active > 0 && (
                                <BreakdownRow dotColor="violet" label="Resterende engangsminutter" value={`${u.one_time_minutes_active} minutter`} testid="bd-onetime" />
                            )}
                        </ul>
                    </div>

                    {/* Right — Denne periode */}
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">DENNE PERIODE</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                            <StatCard icon={Phone} value={s.calls_count || 0} label="opkald" testid="stat-calls" />
                            <StatCard icon={ClipboardCheck} value={s.jobs_count || 0} label="oprettede opgaver" testid="stat-jobs" />
                            <StatCard icon={Clock} value={s.avg_call_duration_label || "—"} label="gennemsnitlig samtaletid" testid="stat-avg" />
                            <StatCard icon={CalendarDays}
                                value={s.last_call_at_label ? "" : "Ingen endnu"}
                                sublabel={s.last_call_at_label || null}
                                label={s.last_call_at_label ? "Seneste opkald:" : "Seneste opkald"}
                                testid="stat-last" />
                        </div>

                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">SYSTEMSTATUS</p>
                        <ul className="space-y-1.5 text-sm">
                            {(data.system_status || []).map((row) => (
                                <SystemStatusRow key={row.label} {...row} />
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Actions */}
                <footer className="px-6 py-4 border-t border-slate-100 bg-white flex flex-wrap gap-2">
                    <TenantActionButton
                        icon={ExternalLink}
                        label="Se opkald"
                        tone="ghost"
                        onClick={() => navigate("/dashboard/jobs")}
                        testid="ai-phone-btn-calls" />
                    <TenantActionButton
                        icon={ShoppingCart}
                        label="Køb ekstra minutter"
                        tone="primary"
                        onClick={() => navigate("/dashboard/settings?tab=billing")}
                        testid="ai-phone-btn-buy" />
                    <TenantActionButton
                        icon={Settings2}
                        label="Administrér abonnement"
                        tone="ghost"
                        onClick={() => navigate("/dashboard/settings?tab=billing")}
                        testid="ai-phone-btn-billing" />
                    <TenantActionButton
                        icon={Pencil}
                        label="Redigér viderestillingsnummer"
                        tone="ghost"
                        onClick={() => setEditOpen(true)}
                        testid="ai-phone-btn-forward" />
                </footer>
            </article>

            {editOpen && (
                <EditForwardDialog
                    currentMasked={data.forward_phone_masked}
                    onClose={() => setEditOpen(false)}
                    onSaved={() => { setEditOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function MetaCell({ icon: Icon, label, value, muted, testid }) {
    return (
        <div className="flex items-start gap-2 min-w-0" data-testid={testid}>
            <Icon className={`w-4 h-4 text-slate-400 shrink-0 mt-0.5`} />
            <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
                <p className={`text-sm mt-0.5 truncate ${muted ? "text-slate-400" : "text-slate-800 font-medium"}`}>{value}</p>
            </div>
        </div>
    );
}

function BreakdownRow({ label, value, testid }) {
    return (
        <li className="flex items-center justify-between" data-testid={testid}>
            <span className="text-slate-600 inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
                {label}:
            </span>
            <span className="text-slate-900 font-medium tabular-nums">{value}</span>
        </li>
    );
}

function StatCard({ icon: Icon, value, sublabel, label, testid }) {
    return (
        <div className="rounded-xl bg-white border border-violet-100 py-3 px-3 flex flex-col items-center text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
             data-testid={testid}>
            {Icon && <Icon className="w-4 h-4 text-violet-500 mb-1.5" />}
            {value && <p className="text-xl font-bold text-slate-900 tabular-nums leading-none">{value}</p>}
            {sublabel && <p className="text-sm font-semibold text-violet-700 mt-1 leading-tight">{sublabel}</p>}
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1 leading-tight">{label}</p>
        </div>
    );
}

function SystemStatusRow({ label, state, value }) {
    const dotCls = {
        ok: "bg-emerald-500",
        ready: "bg-emerald-500",
        error: "bg-rose-500",
        not_configured: "bg-slate-300",
    }[state] || "bg-slate-300";
    const valueCls = {
        ok: "text-emerald-700",
        ready: "text-emerald-700",
        error: "text-rose-700",
        not_configured: "text-slate-500",
    }[state] || "text-slate-500";
    return (
        <li className="flex items-center justify-between" data-testid={`sys-${label.toLowerCase().replace(/\s+/g,'-')}`}>
            <span className="text-slate-600 inline-flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${dotCls}`} />
                {label}
            </span>
            <span className={`font-medium ${valueCls}`}>{value}</span>
        </li>
    );
}

function TenantActionButton({ icon: Icon, label, onClick, tone = "ghost", testid }) {
    const cls = tone === "primary"
        ? "bg-violet-600 hover:bg-violet-700 text-white border-violet-600"
        : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200";
    return (
        <button type="button" onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition ${cls}`}>
            {Icon && <Icon className="w-4 h-4" />}
            {label}
        </button>
    );
}

function EditForwardDialog({ currentMasked, onClose, onSaved }) {
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            await patchTenantForwardPhone(value.trim() || null);
            toast.success("Viderestillingsnummer opdateret");
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Kunne ikke gemme");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-forward-dialog">
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                  className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-base font-bold text-slate-900">Viderestillingsnummer</h3>
                    <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
                </header>
                <div className="p-6 space-y-3 text-sm">
                    <p className="text-slate-600">
                        Kundeopkald viderestilles til dette nummer, når AI-telefonen er midlertidigt spærret eller ikke aktiv.
                    </p>
                    <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">Nyt nummer</label>
                        <input type="tel" value={value} onChange={(e) => setValue(e.target.value)}
                            placeholder={currentMasked || "+45 60 12 34 56"}
                            data-testid="edit-forward-input"
                            autoFocus
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none" />
                        {currentMasked && (
                            <p className="text-xs text-slate-400 mt-1 font-mono">Nuværende: {currentMasked}</p>
                        )}
                    </div>
                </div>
                <footer className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100">Annullér</button>
                    <button type="submit" disabled={saving} data-testid="edit-forward-save"
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Gem
                    </button>
                </footer>
            </form>
        </div>
    );
}
